import { useEffect, useState, useContext } from "react";
import { Outlet, useNavigate, Link } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';
import { ChevronLeft, ChevronRight, Bank, Download } from "../../files/icons/index.js";
import { FirstDayOfMonth, LastDayOfMonth, DatetimeToDate, DateToFormat } from '../../utils/datetime.js';

import { PatientAvatar, Status, Button, Counter } from '../components/component.js';
import Loading from '../components/loading.js';
import Helmet from "../components/helmet.js";

function Accounting() {
  const context = useContext(AppContext);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);

  const [accountTabSelected, setAccountTabSelected] = useState(null);

  const [currentPeriod, setCurrentPeriod] = useState("month"); // month, full
  const [currentPeriodDateStart, setCurrentPeriodDateStart] = useState(FirstDayOfMonth(new Date()));
  const [currentPeriodDateEnd, setCurrentPeriodDateEnd] = useState(LastDayOfMonth(new Date()));

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/accounts', {
        from: currentPeriodDateStart,
        to: currentPeriodDateEnd,
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((accounts, settings) => {
      setAccounts(accounts.data.accounts);
      setSettings(settings.data);
      if (settings.data.accountTemplates.length > 0) {
        setAccountTabSelected(settings.data.accountTemplates[0].id);
      }
      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, [currentPeriodDateStart]);

  function updateCurrentPeriod(direction) {
    if (direction < 0) {
      // Navigate to previous month
      const previousMonth = new Date(currentPeriodDateStart.getFullYear(), currentPeriodDateStart.getMonth() - 1, 1);
      setCurrentPeriodDateStart(FirstDayOfMonth(previousMonth));
      setCurrentPeriodDateEnd(LastDayOfMonth(previousMonth));
    } else {
      // Navigate to next month
      const nextMonth = new Date(currentPeriodDateStart.getFullYear(), currentPeriodDateStart.getMonth() + 1, 1);
      setCurrentPeriodDateStart(FirstDayOfMonth(nextMonth));
      setCurrentPeriodDateEnd(LastDayOfMonth(nextMonth));
    }
  }

  const downloadFile = () => {
    let u = context.addSpiner();
    axios.post('/api/admin/projects/respiteServices/download', {
      from: currentPeriodDateStart,
      to: currentPeriodDateEnd,
    }).then((response) => {
      
      axios.get('/api/admin/files/' + response.data.id, { 
        responseType: 'blob' 
      }).then((responseDownload) => {
        const url = window.URL.createObjectURL(new Blob([responseDownload.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.name + "." + response.data.extension);
        document.body.appendChild(link);
        link.click();
      }).catch((e) => {
        context.addError(e);
        context.removeSpiner(u);
      });

    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderAccounts() {
    // Filter by template
    let filteredAccounts = accounts.filter((account) => {
      // If no account tab is selected, show all accounts
      if (accountTabSelected === null) {
        return true;
      }

      // Otherwise, filter by template
      if (account.templateID === accountTabSelected) {
        return true;
      }

      return false;
    });

    // Filter by transaction date
    filteredAccounts = filteredAccounts.filter((account) => {
      if (account.transactions && account.transactions.length > 0) {
        return account.transactions.some(transaction => {
          const transactionDate = DatetimeToDate(transaction.date);
          return transactionDate >= currentPeriodDateStart && transactionDate <= currentPeriodDateEnd;
        });
      }
      return false;
    });

    if (filteredAccounts.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <div className="typo-normal-150 text-muted">No services in selected period</div>
        </div>
      );
    }

    return filteredAccounts.map((account, index) => {
      let transactions = [];
      if (account.transactions && account.transactions.length > 0) {
        transactions = account.transactions.filter(transaction => {
          const transactionDate = DatetimeToDate(transaction.date);
          return transactionDate >= currentPeriodDateStart && transactionDate <= currentPeriodDateEnd;
        });
      }

      // If no transactions in the current period, skip rendering this account
      if (transactions.length === 0) {
        return null; 
      }

      return (
        <div className="card mb-2">
          <div className="card-header sm">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-start align-items-center w-75">
                <PatientAvatar id={account.userID} alt="logo" style={{height: 30, width: 30}} />
                <div className="item ms-2 me-4" onClick={() => navigate("/patient/" + account.userID+"/card")}>
                  <span className="typo-strong-200">{account.userName}</span>
                </div>
              </div>
              <div className="d-flex justify-content-end align-items-center w-25">
                Balance: <span className="typo-strong-200 ms-2">${account.balance.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {transactions.map((transaction, index) => {
            return (
              <div className="card-content clickable sm" onClick={() => navigate('/patient/' + account.userID + '/card?tab=accounts')}>
                <div key={"account_"+account.id+"_transaction_"+transaction.id} className="d-flex justify-content-between align-items-center">
                  <div className="typo-normal-150"><b>{transaction.title}</b> by <b>{transaction.partnerName}</b> on {DateToFormat(DatetimeToDate(transaction.date), context.dateFormat)}</div>
                  <div className="typo-strong-150">-${transaction.amount}</div>
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Accounting" />

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center">
                <span className="typo-hero-small">Accounting</span>
                {/* <span className="typo-normal-175 mt-2 ms-3">Tracks program attrition metrics, including total enrollment, cases awaiting start, active projects, completed projects, and patient exits</span> */}
              </div>
              <div className="w-25 text-end">
                {/* <div className="item typo-normal-150 ms-2" onClick={() => navigate('/patient/create')}><FilePlus height={16} width={16} background={'none'} fill={'grey'} /> Create Patient</div> */}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">
            
            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex justify-content-start align-items-center w-50">
                    <div className="d-flex justify-content-center align-items-center" style={{width: 20, height: 20}}>
                      <Bank width={16} height={16} color={'grey'} />
                    </div>

                    {settings.accountTemplates.map((template, index) => (
                      <div key={index} className={"item typo-normal-150 ms-2 " + (accountTabSelected === template.id ? "active" : "")} onClick={() => setAccountTabSelected(template.id)}>
                        {template.title}
                      </div>
                    ))}

                    {/* <div className={"item typo-normal-150 ms-2 "  + (chatTabSelected === "clinical" ? "active" : "")} onClick={() => setChatTabSelected("clinical")}>Clinical notes</div> */}

                    {/* <div className="typo-strong-175 ms-2">Respite Services</div> */}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-between align-items-center me-4">
                      <div onClick={() => updateCurrentPeriod(-1)} className="item">
                        <ChevronLeft width={16} height={16} color={'grey'} />
                      </div>
                      <div className="typo-normal-150 text-center ms-2 me-2" style={{width: 100}}>{DateToFormat(DatetimeToDate(currentPeriodDateStart), "MF YYYY")}</div>
                      <div onClick={() => updateCurrentPeriod(1)} className="item">
                        <ChevronRight width={16} height={16} color={'grey'} />
                      </div>
                    </div>
                    <div className="item typo-normal-150 ms-2" onClick={() => downloadFile()}><Download width={16} height={16} color={'grey'} /> Download</div>
                  </div>
                </div>
              </div>
            </div>

            {renderAccounts()}
          </div>
        </div>
      </div>
    </>
  );
}

export default Accounting;
